// src/pages/hcvShortfallShared.js — tokens, formatters and small pieces shared
// by the HCV shortfall tracker pages (/hcv-shortfall and /hcv-shortfall/:code).
// Both pages are intentionally unlinked from the nav, hub pages and footer.

import { useState, useEffect } from 'react';

export const COMPLIANCE_API =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

export const T = {
  blue600: '#1d4ed8', // blue-700: reads at 4.5:1 on the blue-100 chips (WCAG 1.4.3),
  blue100: '#dbeafe',
  blue50: '#eff6ff',
  gray900: '#111827',
  gray800: '#1f2937',
  gray700: '#374151',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray300: '#d1d5db',
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  gray50: '#f9fafb',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  // chart ink (light surface)
  ink: '#0b0b0b',
  ink2: '#52514e',
  muted: '#898781',
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  // series (categorical, fixed order)
  s1: '#2a78d6',
  s2: '#eb6834',
  s3: '#1baf7a',
};

// Status palette: every use pairs the colour with the label, never colour alone.
// Chip text colours sit at 4.5:1 or better on their tints (bold 0.72rem text, WCAG 1.4.3).
export const BUCKETS = {
  exhausted: { label: 'Reserves exhausted', short: 'Exhausted', color: '#7a1414', bg: '#f3dede', icon: '■', blurb: 'Already out: reserves are at or below zero and spending is at or above monthly funding. The hard public signal that an agency is in shortfall now.' },
  shortfall: { label: 'Shortfall risk', short: 'Shortfall', color: '#b42318', bg: '#fbe9e9', icon: '▲', blurb: 'Runs out of HAP funding within 12 months at the current trend, or reserves already exhausted with spending above funding.' },
  watch: { label: 'Watch', short: 'Watch', color: '#9a3f1c', bg: '#fdeee6', icon: '◆', blurb: 'Crosses zero in 12 to 24 months, is spending over funding on thin reserves, or is on HUD’s shortfall-prevention list while spending near the line.' },
  underutilized: { label: 'Underutilized', short: 'Underused', color: '#1d5fb0', bg: '#e6f0fb', icon: '▽', blurb: 'Spending well under funding with units or reserves unused. Leaves money on the table and invites a HUD offset next year.' },
  stable: { label: 'Stable', short: 'Stable', color: '#0a6e0a', bg: '#e6f4e6', icon: '●', blurb: 'Spending close to funding, leasing most ACC units, reserves within HUD’s guideline.' },
  nodata: { label: 'Not modeled', short: 'No data', color: '#5f5e59', bg: '#f0efec', icon: '○', blurb: 'No recent HCV leasing or HAP reported.' },
};
export const BUCKET_ORDER = ['exhausted', 'shortfall', 'watch', 'underutilized', 'stable', 'nodata'];

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
export const fmt = {
  int: (v) => (isNum(v) ? Math.round(v).toLocaleString('en-US') : '—'),
  usd: (v) => (isNum(v) ? (v < 0 ? '−$' : '$') + Math.round(Math.abs(v)).toLocaleString('en-US') : '—'),
  usdShort: (v) => {
    if (!isNum(v)) return '—';
    const a = Math.abs(v); const s = v < 0 ? '−$' : '$';
    if (a >= 1e9) return s + (a / 1e9).toFixed(2) + 'B';
    if (a >= 1e6) return s + (a / 1e6).toFixed(1) + 'M';
    if (a >= 1e3) return s + Math.round(a / 1e3) + 'K';
    return s + Math.round(a);
  },
  pct: (v, d = 0) => (isNum(v) ? (v * 100).toFixed(d) + '%' : '—'),
  pctSigned: (v, d = 1) => (isNum(v) ? (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v * 100).toFixed(d) + '%' : '—'),
  months: (v) => (isNum(v) ? (v < 0 ? '0' : v.toFixed(1)) : '—'),
  month: (ym) => {
    if (!ym) return '—';
    const [y, m] = ym.split('-').map(Number);
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1] + ' ' + y;
  },
};

export function BucketChip({ bucket, size = 'sm', style }) {
  const b = BUCKETS[bucket] || BUCKETS.nodata;
  const pad = size === 'lg' ? '0.35rem 0.85rem' : '0.18rem 0.6rem';
  const fs = size === 'lg' ? '0.9rem' : '0.74rem';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: pad, fontSize: fs, fontWeight: 700, color: b.color, background: b.bg, borderRadius: T.radiusPill, whiteSpace: 'nowrap', letterSpacing: '0.01em', ...style }}>
      <span aria-hidden="true" style={{ fontSize: '0.8em' }}>{b.icon}</span>{b.label}
    </span>
  );
}

export function Header({ mobile, chip, right }) {
  return (
    <header style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${T.gray200}`, padding: mobile ? '0.75rem 1rem' : '1rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', maxWidth: '1280px', margin: '0 auto', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          {chip}
        </div>
        {right && <div style={{ fontSize: '0.78rem', color: T.gray500, textAlign: mobile ? 'left' : 'right' }}>{right}</div>}
      </div>
    </header>
  );
}

export function Card({ children, style }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.gray200}`, borderRadius: T.radius, boxShadow: T.shadowSm, padding: '1.1rem 1.25rem', ...style }}>{children}</div>
  );
}

export function Stat({ label, value, sub, accent, big }) {
  return (
    <Card style={{ padding: '0.9rem 1.1rem', minWidth: 0 }}>
      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: T.gray500, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: big ? '2.1rem' : '1.45rem', fontWeight: 800, color: accent || T.gray900, lineHeight: 1.15, marginTop: '0.25rem', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.76rem', color: T.gray500, marginTop: '0.25rem', lineHeight: 1.35 }}>{sub}</div>}
    </Card>
  );
}
